import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import CloseIcon from '~/icons/components/CloseIcon';
import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import { trackSearchQuerySubmitted } from '~/search/actions';
import { constructorTextStandard } from '~/searchCio/utils/styles';
import { useDebounceRequest } from '~/shared/utils/useDebounce';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Router } from '~/techstyle-shared/next-routes';
import { Button } from '~/techstyle-shared/react-components';
import { defineMessages, useIntl } from '~/techstyle-shared/react-intl';
import { useSessionActions } from '~/techstyle-shared/redux-core';

import {
  QUERY_TYPE_SEARCH_SUBMITTED,
  DEBOUNCE_DELAY,
} from '../constants/cioConstants';
import useCioAutosuggest from '../hooks/useCioAutosuggest';
import { useCioSearchHistory } from '../hooks/useCioSearchHistory';

const SearchForm = styled.form`
  @keyframes openOverlay {
    0% {
      position: relative;
      width: 250px;
      right: 0;
    }
    1% {
      position: fixed;
      width: 250px;
      right: 15%;
      top: 0;
    }
    100% {
      position: fixed;
      width: 500px;
      right: 50%;
      transform: translateX(50%);
      top: 0;
    }
  }
  @keyframes closeOverlay {
    0% {
      position: fixed;
      width: 500px;
      right: 50%;
      transform: translateX(50%);
      top: 0;
    }
    99% {
      position: fixed;
      width: 250px;
      right: 15%;
      top: 0;
    }
    100% {
      position: relative;
      width: 250px;
      right: 0;
    }
  }
  // The Builder Nav has a major breakpoint at 1439px.
  // The margins below stabilize the search input animation.
  // There's potential to harden the Builder Nav and this new Search dropdown
  // to be more resilient. Ticket for this future work: https://techstyle-prod.atlassian.net/browse/FLR-1247

  margin-top: 8px;
  margin-bottom: 8px;

  @media (min-width: 1439px) {
    margin-top: 9.25px;
    margin-bottom: 9.25px;
  }
  width: 250px;
  position: relative;
  animation: ${({ $animation }) => $animation};
  z-index: 10;
  border: 1px solid ${({ theme }) => theme.colors.searchFieldWrapperBorder};
  border-radius: 8px;
  display: flex;
  background-color: ${({ theme: { colors } }) => colors.searchFieldBackground};
  &:focus {
    outline: solid;
  }
`;

const StyledInput = styled.input`
  ${constructorTextStandard}
  width: 100%;
  border: 0;
  line-height: 1.4;
  background-color: ${({ theme: { colors } }) => colors.searchFieldBackground};
  &:focus {
    outline: none;
  }
  /* Hides the default clear button and other decorations in IE/Edge */
  &[type='search']::-ms-clear {
    display: none;
  }
  /* Hides the default clear button and other decorations in WebKit browsers */
  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    display: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.searchCioDesktopInputPlaceholder};
  }
`;

const XButton = styled(Button).attrs({ variant: 'unstyled' })`
  ${constructorTextStandard}
  margin-right: 12px;
  margin-top: 11px;
  margin-bottom: 11px;
  margin-left: 8px;
  width: 16px;
  height: 16px;
  z-index: 11;
  color: ${({ theme }) => theme.colors.linkText};
  visibility: ${({ $inputHasText }) => ($inputHasText ? 'visible' : 'hidden')};
`;

const BeforeInput = styled.div`
  margin: 11px 16px;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const messages = defineMessages({
  search: {
    id: 'search_autosuggest.search_input_placeholder',
    defaultMessage: 'Search',
  },
});
const CioDesktopInput = ({
  animation,
  setOverlay,
  searchValue,
  setSearchValue,
  inputRef,
}) => {
  const intl = useIntl();

  const { updateQuery } = useCioAutosuggest();
  const dispatch = useDispatch();
  const sessionActions = useSessionActions();
  const { addHistory } = useCioSearchHistory();
  const debouncedRequest = useDebounceRequest(() => {
    updateQuery(searchValue || '');
  }, DEBOUNCE_DELAY);

  const secondarySite = useSecondarySite();
  const routePrefix = secondarySite ? `/${secondarySite}` : '';
  const searchPath = `${routePrefix}/search`;

  const handleOnChange = (e) => {
    setSearchValue(e.target.value || '');
    debouncedRequest();
  };

  const clearSearchValue = useCallback(() => {
    setSearchValue('');
    updateQuery('');
    inputRef.current.focus();
  }, [inputRef, setSearchValue, updateQuery]);

  const handleOnKeyDown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Shift') {
      setOverlay(true);
    }

    if (event.key === 'Escape' || (event.key === 'Tab' && event.shiftKey)) {
      clearSearchValue();
      setOverlay(false);
    }
  };

  const handleXButtonKeyDown = (event) => {
    if (event.key === 'Escape') {
      clearSearchValue();
      setOverlay(false);
    }
  };

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (searchValue === '') {
        return;
      }
      setOverlay(false);
      addHistory(searchValue);

      dispatch(
        trackSearchQuerySubmitted({
          query: searchValue,
          queryType: QUERY_TYPE_SEARCH_SUBMITTED,
        })
      );

      sessionActions.updateSessionDetail({
        name: 'search_bar_submission',
        value: '1',
      });
      const searchRoute = {
        pathname: searchPath,
        query: { q: searchValue },
      };
      inputRef.current.blur();
      Router.push(searchRoute);
    },
    [
      addHistory,
      dispatch,
      inputRef,
      searchPath,
      searchValue,
      sessionActions,
      setOverlay,
    ]
  );

  const searchLabel = intl.formatMessage(messages.search);

  return (
    <SearchForm
      $animation={animation}
      onSubmit={handleSubmit}
      data-cnstrc-search-form
    >
      <BeforeInput
        onClick={() => inputRef.current.focus()}
        onMouseDownCapture={(event) => event.preventDefault()}
      >
        <WhitelabelIcon iconName="search" size={16} />
      </BeforeInput>
      <StyledInput
        type="text"
        placeholder={searchLabel}
        aria-label={searchLabel}
        value={searchValue}
        onChange={handleOnChange}
        onFocus={() => setOverlay(true)}
        onClick={() => setOverlay(true)}
        ref={inputRef}
        onKeyDown={handleOnKeyDown}
        data-cnstrc-search-input
        data-testid="cnstrc-search"
      />
      <XButton
        onClick={clearSearchValue}
        onKeyDown={handleXButtonKeyDown}
        $inputHasText={!!searchValue}
        data-testid="cnstrc-search-x"
      >
        <CloseIcon strokeWidth="2" />
      </XButton>
    </SearchForm>
  );
};

CioDesktopInput.propTypes = {
  animation: PropTypes.string,
  inputRef: PropTypes.shape({ current: PropTypes.any }),
  searchValue: PropTypes.string,
  setOverlay: PropTypes.func,
  setSearchValue: PropTypes.func,
};

export default CioDesktopInput;
